<template>
  <div :data-id="panelId" class="flex flex-col">
    <div
      :data-id="panelId"
      class="sticky top-0 z-10 py-6 px-6 flex flex-col gap-2 bg-pure-white"
    >
      <div :data-id="panelId" class="flex items-center justify-between gap-4">
        <BlTextTitle4
          :data-id="panelId"
          :title="creditPackageItem.name"
          :text="creditPackageItem.name"
          class="truncate"
        />
        <div :data-id="panelId" class="flex items-center gap-2">
          <BlChip
            :data-id="panelId"
            :chip="packageItemChips[creditPackageItem.status].chip"
            :color="packageItemChips[creditPackageItem.status].color"
            dense
          />
          <BlIcon icon="close" :clickable="true" />
        </div>
      </div>
      <div
        v-if="!creditPackageItem.is_default"
        :data-id="panelId"
        class="grid grid-cols-2 gap-4"
      >
        <BlInputButton
          :data-id="panelId"
          type="outlined"
          dense
          label="Edit item"
          @clicked="editOpened = true"
        />
        <BlInputButton
          v-if="creditPackageItem.status === 'archived'"
          :data-id="panelId"
          dense
          label="Reactivate item"
          :loading="submitLoading"
          @clicked="onReactivate"
        />
        <BlInputButton
          v-else
          :data-id="panelId"
          type="outlined"
          dense
          label="Archive item"
          :loading="submitLoading"
          @clicked="archiveOpened = true"
        />
      </div>
    </div>

    <div :data-id="panelId" class="px-6 flex flex-col gap-4">
      <div :data-id="panelId" class="flex flex-col gap-2">
        <BlTextBody2
          :data-id="panelId"
          text="Item description:"
          class="text-slate-700"
        />
        <BlTextBody2
          :data-id="panelId"
          :text="creditPackageItem.description"
          class="whitespace-pre-line"
        />
      </div>
      <div
        v-if="creditPackageItem.status === 'archived'"
        :data-id="panelId"
        class="flex flex-col gap-2"
      >
        <BlTextBody2 :data-id="panelId" text="Status:" class="text-slate-700" />
        <div :data-id="panelId">
          <BlTextBody2 :data-id="panelId" text="Archived on" />
          <BlTextBody2
            :data-id="panelId"
            :text="
              displayDate({
                date: creditPackageItem.archived_at,
                format: 'DD MMMM YYYY'
              })
            "
          />
          <BlTextBody2 :data-id="panelId" text="by" />
          <BlTextBody2
            v-if="
              !creditPackageItem.archiver || !creditPackageItem.archiver.name
            "
            :data-id="panelId"
            text="Deleted user"
            class="italic"
          />
          <BlTextBody2
            v-else
            :data-id="panelId"
            :text="creditPackageItem.archiver.name"
          />
        </div>
      </div>
      <div
        :data-id="panelId"
        class="py-3 px-3 flex items-center justify-between bg-prim-100 rounded"
      >
        <BlTextBody3
          :data-id="panelId"
          text="Standard retail price (per credit)"
          class="text-light-purple-400"
        />
        <div :data-id="panelId">
          <BlTextSubtitle2
            :data-id="panelId"
            :text="currentCenter.currency_sym"
            class="text-dark-purple-100"
          />
          <BlTextSubtitle2
            :data-id="panelId"
            :text="priceFormats(creditPackageItem.standard_retail_price)"
            class="text-dark-purple-100"
          />
        </div>
      </div>
      <div v-if="!creditPackageItem.users.length" :data-id="panelId">
        <BlTextBody2
          :data-id="panelId"
          text="This item can be applied to appointments with any therapist."
          class="text-slate-700"
        />
      </div>
      <div v-else :data-id="panelId" class="pb-16 flex flex-col gap-2">
        <div :data-id="panelId">
          <BlTextBody2
            :data-id="panelId"
            text="This item is restricted to appointments assigned to the"
            class="text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="creditPackageItem.users.length"
            class="font-bold text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            text="selected therapist(s):"
            class="text-slate-700"
          />
        </div>
        <div :data-id="panelId" class="flex flex-col">
          <div
            v-for="user in creditPackageItem.users"
            :key="user.id"
            :data-id="panelId"
            class="py-3 flex items-center justify-between gap-4 border-b border-slate-400"
          >
            <BlTextSubtitle3
              v-if="!user.name"
              :data-id="panelId"
              title="Deleted user"
              text="Deleted user"
              class="truncate text-slate-800 italic"
            />
            <BlTextSubtitle3
              v-else
              :data-id="panelId"
              :title="user.name"
              :text="user.name"
              class="truncate text-slate-800"
            />
            <BlTextBody2
              :data-id="panelId"
              :text="user.email"
              class="text-slate-700"
            />
          </div>
        </div>
      </div>
    </div>

    <EditPackageItemModal
      v-if="editOpened"
      :creditPackageItemId="creditPackageItem.id"
      @close-modal="editOpened = false"
    />

    <ArchivePackageItemModal
      v-if="archiveOpened"
      :creditPackageItem="creditPackageItem"
      @close-modal="archiveOpened = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import EditPackageItemModal from "./EditPackageItemModal.vue";
import ArchivePackageItemModal from "./ArchivePackageItemModal.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "PackageItemPanel",
  components: {
    EditPackageItemModal,
    ArchivePackageItemModal,
    //
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlIcon,
    BlChip,
    BlInputButton,
    BlBackdrop
  },
  props: {
    creditPackageItem: { type: Object },
    panelId: { type: String }
  },
  data() {
    return {
      packageItemChips: {
        active: { chip: "Active", color: "lime" },
        archived: { chip: "Archived", color: "rose" }
      },
      providers: {
        available_for_everyone: "Available for everyone",
        restricted: "Restricted"
      },
      submitLoading: false,
      editOpened: false,
      archiveOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentCenter"])
  },
  methods: {
    ...mapActions(["updateCreditPackageItem"]),
    displayDate,
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.currentCenter.currency_settings;
      return this.displayPrice({ settings, price });
    },
    async onReactivate() {
      const params = {
        id: this.creditPackageItem.id,
        package_item: { status: "active" },
        title: "Item activated",
        message: `Success! The <b>${this.creditPackageItem.name}</b> has been activated.`
      };

      this.submitLoading = true;
      await this.updateCreditPackageItem(params);
      this.submitLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/packageItem/PackageItemPanel.vue